import { useState, useEffect, useCallback } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { StatusChip } from "@/components/StatusChip";
import { EmptyState } from "@/components/Primitives";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trash2, RotateCcw, AlertTriangle, Archive } from "lucide-react";

const RESOURCES = [
  { key: "leads", label: "Leads", nameKey: "business_name", protected: false },
  { key: "businesses", label: "Businesses", nameKey: "name", protected: false },
  { key: "contacts", label: "Contacts", nameKey: "first_name", protected: false },
  { key: "tasks", label: "Tasks", nameKey: "title", protected: false },
  { key: "opportunities", label: "Opportunities", nameKey: "name", protected: false },
  { key: "contracts", label: "Contracts (protected)", nameKey: "contract_number", protected: true },
];

export default function TrashManager() {
  const { can } = useAuth();
  const isOwner = can("tenant_owner", "platform_owner");
  const [resource, setResource] = useState("leads");
  const [rows, setRows] = useState([]);
  const cfg = RESOURCES.find((r) => r.key === resource);

  const load = useCallback(() => {
    api.get(`/${resource}?archived=true`).then((r) => setRows(r.data.items)).catch(() => setRows([]));
  }, [resource]);
  useEffect(() => { load(); }, [load]);

  const restore = async (row) => { try { await api.post(`/${resource}/${row.id}/restore`); toast.success("Restored"); load(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } };
  const purge = async (row) => {
    const nm = row[cfg.nameKey] || row.id;
    if (!window.confirm(`PERMANENTLY DELETE "${nm}"?\n\nThis cannot be undone. The record and its data will be removed forever.`)) return;
    try { await api.delete(`/${resource}/${row.id}/purge`); toast.success("Permanently deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-trash">
      <div className="flex items-center gap-3 mb-4">
        <Select value={resource} onValueChange={setResource}>
          <SelectTrigger className="w-full sm:w-64" data-testid="trash-resource"><SelectValue /></SelectTrigger>
          <SelectContent>{RESOURCES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-400">{rows.length} archived</span>
      </div>

      {cfg.protected && (
        <div className="mb-4 rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3 text-xs text-[#B45309] flex items-center gap-2" data-testid="trash-protected-note">
          <AlertTriangle className="h-4 w-4 shrink-0" />Contracts and commissions are business-critical history. They can be archived and restored, but never permanently deleted.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="cc-card"><EmptyState icon={Archive} title="Trash is empty" hint="Archived records appear here and can be restored." /></div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
          {rows.map((row) => (
            <div key={row.id} className="flex items-center justify-between px-4 py-3" data-testid={`trash-row-${row.id}`}>
              <div className="min-w-0"><p className="text-sm font-medium text-slate-800 truncate">{row[cfg.nameKey] || "—"}</p><p className="text-xs text-slate-400">Archived</p></div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => restore(row)} data-testid={`trash-restore-${row.id}`}><RotateCcw className="h-3 w-3" />Restore</Button>
                {isOwner && !cfg.protected && (
                  <Button size="sm" variant="outline" className="h-7 text-xs text-[#C42607]" onClick={() => purge(row)} data-testid={`trash-purge-${row.id}`}><Trash2 className="h-3 w-3" />Delete Forever</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
